// src/pages/Donations.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, TextField,
  Grid, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, MenuItem,
  TablePagination, Avatar, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Snackbar, Alert, CircularProgress
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import apiEndpoints from '../../apiconfig';

const donationColumns = [
  { id: 'id', label: 'ID', minWidth: 70 },
  { id: 'donorName', label: 'Donor Name', minWidth: 180 },
  { id: 'date', label: 'Date', minWidth: 120 },
  { id: 'campaign', label: 'Campaign', minWidth: 180 },
  { id: 'amount', label: 'Amount (₹)', minWidth: 120, align: 'right' },
  { id: 'paymentMethod', label: 'Payment Method', minWidth: 150 },
  { id: 'status', label: 'Status', minWidth: 120 },
  { id: 'actions', label: 'Receipt', minWidth: 100, align: 'center' },
];

const Donations = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchDonations = async () => {
      try {
        const params = new URLSearchParams();
        if (searchText) params.append('search', searchText);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        const response = await fetch(`${apiEndpoints.donate}?uniqueDonors=false${params.toString()}`);
        const data = await response.json();

        const transformedDonations = data.map((donation) => ({
          id: donation.id,
          donorName: donation.donar_name,
          date: formatDate(donation.lastdonationdate),
          campaign: donation.campaign || 'General Donation',
          amount: parseFloat(donation.amount),
          paymentMethod: donation.payment_method || 'Online',
          status: donation.status || 'Completed',
          donorEmail: donation.donar_email,
          donorPhone: donation.mobile,
          panNumber: donation.pan_number,
          aadharNumber: donation.addhar_number,
          address: donation.address,
          notes: `Donation received via ${donation.payment_method || 'Online'}`
        }));

        setDonations(transformedDonations);
      } catch (error) {
        console.error('Error fetching donations:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load donation data',
          severity: 'error'
        });
        setDonations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDonations();
  }, [searchText, filters.startDate, filters.endDate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      }
      if (typeof dateString === 'string' && dateString.match(/\d{1,2} \w{3} \d{4}/)) {
        return dateString;
      }
      return 'N/A';
    } catch {
      return 'N/A';
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
const handleDownloadReceipt = (donationId) => {
  // Navigate to the receipt page with the donation ID
  window.open(`https://dcpwtrust.com/donation-receipt?id=${donationId}`, '_blank');
};
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearch = (e) => setSearchText(e.target.value);

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
    });
    setSearchText('');
  };

  const handleViewDetails = (donation) => {
    setSelectedDonation(donation);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsDialog(false);
    setSelectedDonation(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleExport = () => {
    try {
      const headers = ['ID', 'Donor Name', 'Date', 'Campaign', 'Amount (₹)', 'Payment Method', 'Status'];
      const csvContent = [
        headers.join(','),
        ...filteredDonations.map(donation =>
          [
            donation.id,
            `"${donation.donorName}"`,
            donation.date,
            `"${donation.campaign}"`,
            donation.amount,
            donation.paymentMethod,
            donation.status,
          ].join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `donations_export_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: 'Donation data exported successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to export donation data',
        severity: 'error'
      });
    }
  };

  const filteredDonations = donations.filter(donation => {
    return (
      (filters.startDate ? new Date(donation.date) >= new Date(filters.startDate) : true) &&
      (filters.endDate ? new Date(donation.date) <= new Date(filters.endDate) : true) &&
      (searchText ?
        donation.donorName.toLowerCase().includes(searchText.toLowerCase()) ||
        donation.campaign.toLowerCase().includes(searchText.toLowerCase()) ||
        donation.paymentMethod.toLowerCase().includes(searchText.toLowerCase()) : true)
    );
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>Donations</Typography>

      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search donations..."
              value={searchText}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              name="startDate"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              name="endDate"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} md={4} display="flex" gap={1}>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FilterListIcon />}
              onClick={handleResetFilters}
              sx={{ flex: 1 }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ flex: 1 }}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {donationColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth, fontWeight: 600 }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDonations
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    // onClick={() => handleViewDetails(row)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    {donationColumns.map((column) => {
                      const value = row[column.id];

                      if (column.id === 'id') {
                        const serialNumber = page * rowsPerPage + filteredDonations.findIndex(d => d.id === row.id) + 1;
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{ color: 'success.main', fontWeight: 500 }}
                          >
                            {serialNumber}
                          </TableCell>
                        );
                      }

                      if (column.id === 'amount') {
                        return (
                          <TableCell key={column.id} align="right">
                            <Typography fontWeight={600}>₹{value.toLocaleString()}</Typography>
                          </TableCell>
                        );
                      }

                      if (column.id === 'status') {
                        return (
                          <TableCell key={column.id}>
                            <Typography
                              color={
                                value === 'Completed' ? 'success.main' :
                                  value === 'Pending' ? 'warning.main' :
                                    'error.main'
                              }
                              fontWeight={500}
                            >
                              {value}
                            </Typography>
                          </TableCell>
                        );
                      }

                      if (column.id === 'actions') {
                        return (
                          <TableCell
                            key={column.id}
                            align="center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReceipt(row.id)}
                              color="success"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={column.id} align={column.align}>
                          {value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredDonations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Donation Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={handleCloseDetails}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedDonation ? `Donation Details` : 'Loading...'}
            </Typography>
            <IconButton onClick={handleCloseDetails}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {selectedDonation && (
            <Box>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab label="Transaction Details" />
                <Tab label="Donor Information" />
              </Tabs>

              {activeTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Donation ID</Typography>
                    <Typography>{selectedDonation.id}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Date</Typography>
                    <Typography>{selectedDonation.date}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Campaign</Typography>
                    <Typography>{selectedDonation.campaign}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                    <Typography variant="h6" color="primary">
                      ₹{selectedDonation.amount.toLocaleString()}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Payment Method</Typography>
                    <Typography>{selectedDonation.paymentMethod}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                    <Typography
                      color={
                        selectedDonation.status === 'Completed' ? 'success.main' :
                          selectedDonation.status === 'Pending' ? 'warning.main' :
                            'error.main'
                      }
                      fontWeight={500}
                    >
                      {selectedDonation.status}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {activeTab === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>Donor Information</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                    <Typography>{selectedDonation.donorName}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                    <Typography>{selectedDonation.donorEmail}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                    <Typography>{selectedDonation.donorPhone}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">PAN Number</Typography>
                    <Typography>{selectedDonation.panNumber || 'Not provided'}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                    <Typography>{selectedDonation.address || 'Not provided'}</Typography>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDetails} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Donations;